import React from 'react';
import {
  MobileHeader, Card, Button, Select, SkeletonCard, ErrorState, useTheme,
} from '@parsina/framework';
import { THEME_PRESETS, FONT_SIZES } from '@parsina/framework/tokens';
import { LogOut } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useMe } from '../../hooks/useMe.js';
import { logout } from '../../lib/auth.js';
import { fmtParseh } from '../../constants/pricing';
import { MobileSectionTitle, MobileInfoRow } from '../../components/mobile/MobileKit.jsx';
import { IDENTITY_FIELDS, identityValues } from './Profile.shared.js';
import ProfileIdentityForm from './ProfileIdentityForm.jsx';

// پروفایل — the phone profile, a top-level bottom-nav destination.
//
// The desktop's two cards become two stacked sections, and every fact gets its
// own full-width row: a long value — a UUID, a phone number — must never be the
// thing that forces a 320px viewport into horizontal scroll.
//
// Sign-out is a full-width button at the BOTTOM rather than a header action. It
// is the one destructive control on the screen, and a 44px square on the gradient
// beside «پروفایل» is exactly the shape a thumb hits by accident when reaching
// for the top of the list.

export default function ProfileMobile() {
  const { t, locale, setLocale, formatNumber, formatDigits } = useTranslation();
  const { me, balance, loading, error, reload } = useMe();
  const { theme, fontSize, setAppearance } = useTheme();

  const values = identityValues({ me, balance, t, formatNumber, formatDigits, fmtParseh });

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }} data-testid="profile-scroll">
        <MobileHeader
          title={t('profile.title')}
          user={{ name: me?.fullName || '' }}
          greeting={t('mobile.greeting')}
        />

        <div style={{ padding: '0.875rem 1rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* ── مشخصات (editable) ── */}
          <ProfileIdentityForm phone={me?.phone || ''} />

          {/* ── حساب کاربری (derived, read-only) ── */}
          <div>
            <MobileSectionTitle>{t('profile.sections.account')}</MobileSectionTitle>
            {error ? (
              <ErrorState
                title={t('errors.loadFailed')}
                onRetry={reload}
                retryLabel={t('common.actions.retry')}
              />
            ) : loading && !me ? (
              <SkeletonCard />
            ) : (
              <Card>
                {IDENTITY_FIELDS.map((field) => (
                  <MobileInfoRow
                    key={field.id}
                    label={t(field.labelKey)}
                    value={values[field.id]}
                    ltr={field.ltr}
                  />
                ))}
                <p style={{ margin: '0.625rem 0 0', fontSize: '0.6875rem', lineHeight: 1.9, color: C.textMuted }}>
                  {t('profile.identity.note')}
                </p>
              </Card>
            )}
          </div>

          {/* ── ظاهر و زبان ── */}
          <div>
            <MobileSectionTitle>{t('profile.sections.appearance')}</MobileSectionTitle>
            <Card style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: CT.ink2, marginBottom: '0.5rem' }}>
                  {t('profile.appearance.theme')}
                </div>
                <div
                  role="radiogroup"
                  aria-label={t('profile.appearance.theme')}
                  style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: 2 }}
                >
                  {THEME_PRESETS.map((preset) => {
                    const active = preset.id === theme;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setAppearance({ theme: preset.id })}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          flexShrink: 0,
                          minHeight: '2.75rem',
                          padding: '0 0.75rem',
                          border: `1.5px solid ${active ? C.teal : C.border}`,
                          borderRadius: 10,
                          background: C.card,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: '0.75rem',
                          fontWeight: active ? 800 : 500,
                          color: active ? CT.accentInk : C.textSecondary,
                        }}
                      >
                        <span
                          aria-hidden
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${preset.swatch2}, ${preset.swatch})`,
                          }}
                        />
                        {t(`profile.theme.${preset.id}`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Select
                label={t('profile.appearance.fontSize')}
                value={fontSize}
                onChange={(e) => setAppearance({ fontSize: e.target.value })}
                options={FONT_SIZES.map((f) => ({ value: f.id, label: t(`profile.fontSize.${f.id}`) }))}
                placeholder=""
              />

              <Select
                label={t('profile.appearance.language')}
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                options={[
                  { value: 'fa', label: t('profile.language.fa') },
                  { value: 'en', label: t('profile.language.en') },
                ]}
                placeholder=""
              />

              <p style={{ margin: 0, fontSize: '0.6875rem', lineHeight: 1.9, color: C.textMuted }}>
                {t('profile.appearance.localOnly')}
              </p>
            </Card>
          </div>

          <Button
            variant="outline"
            onClick={() => logout()}
            className="w-full min-h-12"
            data-testid="profile-logout"
          >
            <LogOut size={16} aria-hidden />
            {t('profile.logout')}
          </Button>
        </div>
      </div>
    </div>
  );
}
